if (!customElements.get('klaim-sticky-atc')) {
  customElements.define(
    'klaim-sticky-atc',
    class KlaimStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.stickyButton = this.querySelector('.klaim-sticky-atc__button');
        this.stickyButtonLabel = this.querySelector('.klaim-sticky-atc__button-label');
        this.stickyButtonPrice = this.querySelector('.klaim-sticky-atc__button-price');
        this.sizesRoot = this.querySelector('[data-klaim-sticky-sizes]');
        this.standardSelector = this.querySelector('[data-klaim-sticky-selector]');
        this.standardSizeToggle = this.querySelector('[data-klaim-sticky-size-toggle]');
        this.standardVariantTray = this.querySelector('[data-klaim-sticky-variant-tray]');
        this.standardVariantList = this.querySelector('[data-klaim-sticky-variant-list]');
        this.standardSizeIndex = Number(this.standardSelector?.dataset.klaimStickySizeIndex || 0);
        this.standardTrayOpen = false;
        this.bundlePickersRoot = this.querySelector('[data-klaim-sticky-bundle-pickers]');
        this.bundleToggle = this.querySelector('[data-klaim-sticky-bundle-toggle]');
        this.bundlePickersOpen = false;
        this.expressPayButton = this.querySelector('[data-klaim-express-pay]');
        this.productInfo = this.closest('product-info');
        this.componentControls = Array.from(this.querySelectorAll('[data-klaim-component-control]'));
        this.desktopButton = this.productInfo?.querySelector('[data-klaim-desktop-add]');
        this.stockMap = {};
        this.lastBundleSignature = '';
        this.bundleBuilder = null;

        try {
          const stockNode = this.productInfo?.querySelector('[data-klaim-stock-map]');
          if (stockNode) this.stockMap = JSON.parse(stockNode.textContent || '{}');
        } catch (error) {
          this.stockMap = {};
        }

        if (!this.stickyButton || !this.productInfo) return;

        this.getComponentPicker = () => this.productInfo.querySelector('[data-klaim-pdp-flaneur] variant-selects');
        this.getComponentOptions = () => Array.from(this.getComponentPicker()?.querySelectorAll('select') || []).map(select => select.value);
        this.syncComponentOptions = () => {
          if (!this.componentControls.length) return true;
          const options = this.getComponentOptions();
          const selected = this.stockMap[String(this.getSelectedVariantId())];
          this.componentControls.forEach((control, index) => {
            const summary = control.querySelector('[data-klaim-component-label]');
            const text = `${control.dataset.label} ${options[index] || ''}`.trim();
            if (summary.textContent !== text) summary.textContent = text;
            control.querySelectorAll('[data-klaim-component-value]').forEach(button => {
              const value = button.dataset.klaimComponentValue;
              const available = Object.values(this.stockMap).some(variant => variant.online && variant.options[index] === value);
              if (button.disabled === available) button.disabled = !available;
              const selected = String(value === options[index]);
              if (button.getAttribute('aria-pressed') !== selected) button.setAttribute('aria-pressed', selected);
              const stock = button.querySelector('[data-klaim-component-stock]');
              const label = available ? '' : 'Sold out';
              if (stock.textContent !== label) stock.textContent = label;
            });
          });
          return options.length === this.componentControls.length && options.every((value, index) => value === selected?.options?.[index]);
        };
        this.closeComponentMenus = (focus = false) => {
          this.componentControls.forEach(control => {
            const toggle = control.querySelector('[data-klaim-component-toggle]');
            if (focus && toggle.getAttribute('aria-expanded') === 'true') toggle.focus();
            toggle.setAttribute('aria-expanded', 'false');
            control.querySelector('[data-klaim-component-menu]').hidden = true;
          });
        };
        this.handleComponentClick = event => {
          const control = event.target.closest('[data-klaim-component-control]');
          if (!control) return;
          const toggle = control.querySelector('[data-klaim-component-toggle]');
          const menu = control.querySelector('[data-klaim-component-menu]');
          if (event.target.closest('[data-klaim-component-toggle]')) {
            const open = menu.hidden;
            this.closeComponentMenus();
            menu.hidden = !open;
            toggle.setAttribute('aria-expanded', String(open));
            if (open) (menu.querySelector('[aria-pressed="true"]:not(:disabled)') || menu.querySelector('button:not(:disabled)'))?.focus();
            return;
          }
          const choice = event.target.closest('[data-klaim-component-value]');
          if (!choice || choice.disabled) return;
          const source = this.getComponentPicker()?.querySelectorAll('select')[Number(control.dataset.klaimComponentControl)];
          if (source && source.value !== choice.dataset.klaimComponentValue) {
            source.value = choice.dataset.klaimComponentValue;
            source.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.closeComponentMenus();
          toggle.focus({ preventScroll: true });
          this.updateStickyState();
        };

        this.getBundleBuilder = () => this.productInfo.querySelector('.yeezy-slide-builder, .klaim-set-builder');
        this.getBundleButton = (builder) => builder?.querySelector('[data-yeezy-cta], [data-set-submit]');
        this.getPrimaryButton = () => {
          const builder = this.getBundleBuilder();
          return this.getBundleButton(builder) || this.productInfo.querySelector('.product-form__submit, button[type="submit"]');
        };

        this.extractPrice = (value) => {
          const matches = String(value || '').match(/(?:€\s*)?\d{1,3}(?:[.\s]\d{3})*(?:[,\.]\d{2})?\s*€/g) ||
            String(value || '').match(/€\s*\d{1,3}(?:[.\s]\d{3})*(?:[,\.]\d{2})?/g) || [];
          return matches.length ? matches[matches.length - 1].replace(/\s+/g, ' ').trim() : '';
        };

        this.cleanActionLabel = (value) => String(value || '')
          .replace(/(?:€\s*)?\d{1,3}(?:[.\s]\d{3})*(?:[,\.]\d{2})?\s*€/g, '')
          .replace(/€\s*\d{1,3}(?:[.\s]\d{3})*(?:[,\.]\d{2})?/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase();

        this.getCurrentPrice = () => {
          const price = this.productInfo.querySelector(
            '.klaim-pdp-flaneur__price .price-item--sale:not(:empty), .klaim-pdp-flaneur__price .price-item--regular:not(:empty)'
          );
          return price?.textContent.replace(/\s+/g, ' ').trim() || '';
        };

        this.getSelectedVariantId = () => {
          const input = this.productInfo.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"]');
          return input?.value || '';
        };

        this.isCurrentVariantAvailable = (button) => {
          const stock = this.stockMap[String(this.getSelectedVariantId())];
          if (stock && typeof stock.online === 'boolean') return stock.online;
          return button ? !button.disabled : false;
        };

        this.setStickyCopy = (label, price) => {
          if (this.stickyButtonLabel) this.stickyButtonLabel.textContent = label;
          else this.stickyButton.textContent = label;
          if (this.stickyButtonPrice) this.stickyButtonPrice.textContent = price;
        };

        this.getSelectedOptions = () => {
          const selected = this.stockMap[String(this.getSelectedVariantId())];
          if (selected && Array.isArray(selected.options)) return selected.options;

          const picker = this.productInfo.querySelector('variant-selects');
          if (!picker) return [];
          return Array.from(picker.children)
            .filter((element) => element.matches('fieldset, .product-form__input--dropdown'))
            .map((group) => {
              const checked = group.querySelector('input[type="radio"]:checked');
              const select = group.querySelector('select');
              return checked?.value || select?.value || '';
            });
        };

        this.syncStandardVariantRows = () => {
          if (!this.standardVariantList) return;
          const selectedId = String(this.getSelectedVariantId());
          const selectedOptions = this.getSelectedOptions();
          const buttons = Array.from(this.standardVariantList.querySelectorAll('[data-klaim-sticky-variant-id]'));
          let visibleCount = 0;

          buttons.forEach((button) => {
            let options = [];
            try {
              options = JSON.parse(button.dataset.klaimStickyVariantOptions || '[]');
            } catch (error) {}
            const compatible = !selectedOptions.length || options.every((value, index) => {
              return index === this.standardSizeIndex || String(value) === String(selectedOptions[index] || '');
            });
            const item = button.closest('li');
            if (item && item.hidden === compatible) item.hidden = !compatible;
            if (compatible) visibleCount += 1;
            const isSelected = String(button.dataset.klaimStickyVariantId) === selectedId;
            if (button.getAttribute('aria-pressed') !== String(isSelected)) {
              button.setAttribute('aria-pressed', String(isSelected));
            }
          });

          if (!visibleCount && buttons.length) {
            buttons.forEach((button) => {
              const item = button.closest('li');
              if (item) item.hidden = false;
            });
            visibleCount = buttons.length;
          }
          this.standardVariantTray?.style.setProperty(
            '--klaim-sticky-variant-height',
            `${Math.min(visibleCount * 36 + 8, 520)}px`
          );
        };

        this.openStandardVariantTray = () => {
          if (!this.standardVariantTray || !this.standardSizeToggle) return;
          this.syncStandardVariantRows();
          this.standardTrayOpen = true;
          this.standardVariantTray.dataset.open = 'true';
          this.standardVariantTray.setAttribute('aria-hidden', 'false');
          this.standardSizeToggle.setAttribute('aria-expanded', 'true');
          this.setAttribute('data-standard-size-open', 'true');
          requestAnimationFrame(() => {
            const selected = this.standardVariantList?.querySelector('li:not([hidden]) [aria-pressed="true"]');
            const first = Array.from(this.standardVariantList?.querySelectorAll('li:not([hidden]) button') || [])[0];
            (selected || first)?.focus({ preventScroll: true });
            window.setTimeout(() => {
              if (this.standardTrayOpen) selected?.scrollIntoView({ block: 'nearest' });
            }, 310);
          });
        };

        this.closeStandardVariantTray = (restoreFocus = false) => {
          if (!this.standardVariantTray || !this.standardSizeToggle) return;
          this.standardTrayOpen = false;
          this.standardVariantTray.dataset.open = 'false';
          this.standardVariantTray.setAttribute('aria-hidden', 'true');
          this.standardSizeToggle.setAttribute('aria-expanded', 'false');
          this.removeAttribute('data-standard-size-open');
          if (restoreFocus) this.standardSizeToggle.focus({ preventScroll: true });
        };

        this.unbindBundleBuilder = () => {
          if (!this.bundleBuilder) return;
          this.bundleBuilder.removeEventListener('change', this.handleBundleInput);
          this.bundleBuilder.removeEventListener('click', this.handleBundleClick);
          this.bundleBuilder = null;
        };

        this.bindBundleBuilder = (builder) => {
          if (!builder || builder === this.bundleBuilder) return;
          this.unbindBundleBuilder();
          this.bundleBuilder = builder;
          this.handleBundleInput = () => {
            this.lastBundleSignature = '';
            this.updateStickyState();
          };
          this.handleBundleClick = (event) => {
            if (!event.target.closest('[data-pack], [data-size-value], [data-set-submit], select, button')) return;
            this.lastBundleSignature = '';
            requestAnimationFrame(this.updateStickyState);
          };
          builder.addEventListener('change', this.handleBundleInput);
          builder.addEventListener('click', this.handleBundleClick);
        };

        this.getBundleState = () => {
          const builder = this.getBundleBuilder();
          const button = this.getBundleButton(builder);
          if (!builder || !button) return null;
          const text = button.textContent.replace(/\s+/g, ' ').trim();
          const selectedPack = builder.querySelector('[data-pack].is-selected, [data-pack][aria-pressed="true"]');
          const packText = selectedPack?.textContent.replace(/\s+/g, ' ').trim() || text;
          const isEssentials = builder.classList.contains('klaim-set-builder');
          const selectionComplete = builder.dataset.selectionComplete !== 'false';
          const summaryText = isEssentials && selectionComplete ? packText : text;
          return {
            builder,
            button,
            available: !button.disabled,
            label: this.cleanActionLabel(summaryText) || 'ADD TO BAG',
            price: this.extractPrice(summaryText)
          };
        };

        this.copySelectOptions = (source, target) => {
          if (!source || !target) return;
          target.innerHTML = source.innerHTML;
          target.value = source.value;
        };

        this.renderYeezySizes = (builder) => {
          const target = this.bundlePickersRoot || this.sizesRoot;
          const panels = Array.from(builder.querySelectorAll('[data-size-panel]'));
          target.innerHTML = panels.length ? panels.map((panel, index) => {
            const title = panel.querySelector('.yeezy-slide-builder__size-head span')?.textContent.trim() || `PAIR ${index + 1}`;
            const selected = panel.querySelector('.yeezy-slide-builder__size.is-selected')?.dataset.sizeValue || '';
            const options = Array.from(panel.querySelectorAll('[data-size-value]')).map((source) => {
              const value = source.dataset.sizeValue || '';
              return `<option value="${this.escapeAttribute(value)}"${source.disabled ? ' disabled' : ''}${value === selected ? ' selected' : ''}>${this.escapeHtml(value)}${source.disabled ? ' - SOLD OUT' : ''}</option>`;
            }).join('');
            return `<label class="klaim-sticky-atc__bundle-row" data-bundle-size-panel="${this.escapeAttribute(panel.dataset.sizePanel || '')}">
              <span class="klaim-sticky-atc__bundle-label">${this.escapeHtml(title)}</span>
              <select class="klaim-sticky-atc__bundle-select" data-bundle-size-select aria-label="${this.escapeAttribute(title)}">${options}</select>
            </label>`;
          }).join('') : '<span class="klaim-sticky-atc__bundle-empty">CHOOSE YOUR PAIRS ABOVE</span>';
        };

        this.renderEssentialsSizes = (builder) => {
          const target = this.bundlePickersRoot || this.sizesRoot;
          const items = Array.from(builder.querySelectorAll('[data-set-items] .klaim-set-builder__item'));
          target.innerHTML = items.length ? items.map((item, index) => {
            const productTitle = item.querySelector('[data-selected-product]')?.textContent.trim() || item.dataset.productHandle || `SET ${index + 1}`;
            const shirtSource = item.querySelector('[data-shirt-size]');
            const shortSource = item.querySelector('[data-short-size]');
            const buildField = (source, key, label) => {
              if (!source) return '';
              const options = Array.from(source.options || []).map((option) => `<option value="${this.escapeAttribute(option.value)}"${option.disabled ? ' disabled' : ''}${option.value === source.value ? ' selected' : ''}>${this.escapeHtml(option.textContent.trim())}</option>`).join('');
              return `<label class="klaim-sticky-atc__bundle-field">
                <span>${label}</span>
                <select class="klaim-sticky-atc__bundle-select" data-bundle-set-select="${key}" aria-label="${this.escapeAttribute(`${productTitle} ${label}`)}">${options}</select>
              </label>`;
            };
            return `<div class="klaim-sticky-atc__bundle-row klaim-sticky-atc__bundle-row--double" data-bundle-set-index="${index}">
              <span class="klaim-sticky-atc__bundle-label">SET ${String(index + 1).padStart(2, '0')} · ${this.escapeHtml(productTitle)}</span>
              <span class="klaim-sticky-atc__bundle-controls">${buildField(shirtSource, 'shirt', 'SHIRT')}${buildField(shortSource, 'short', 'SHORT')}</span>
            </div>`;
          }).join('') : '<span class="klaim-sticky-atc__bundle-empty">CHOOSE YOUR SET ABOVE</span>';
        };

        this.renderBundleSizes = (builder) => {
          if (!this.sizesRoot) return;
          const signature = this.getBundleSignature(builder);
          if (signature === this.lastBundleSignature) return;
          this.lastBundleSignature = signature;
          if (builder.classList.contains('yeezy-slide-builder')) this.renderYeezySizes(builder);
          else this.renderEssentialsSizes(builder);
          if (this.bundlePickersRoot) {
            this.bundlePickersRoot.hidden = !this.bundlePickersOpen;
            this.setAttribute('data-bundle-open', this.bundlePickersOpen ? 'true' : 'false');
          }
        };

        this.getBundleSignature = (builder) => {
          if (!builder) return '';
          if (builder.classList.contains('yeezy-slide-builder')) {
            return Array.from(builder.querySelectorAll('[data-size-panel]')).map((panel) => {
              const selected = panel.querySelector('.yeezy-slide-builder__size.is-selected')?.dataset.sizeValue || '';
              return `${panel.dataset.sizePanel}:${selected}`;
            }).join('|');
          }
          return Array.from(builder.querySelectorAll('[data-set-items] .klaim-set-builder__item')).map((item) => {
            return `${item.dataset.productHandle}:${item.querySelector('[data-shirt-size]')?.value || ''}:${item.querySelector('[data-short-size]')?.value || ''}`;
          }).join('|');
        };

        this.renderStandardSize = () => {
          const sizeButton = this.sizesRoot?.querySelector('.klaim-sticky-atc__size');
          if (!sizeButton) return;
          if (sizeButton.classList.contains('klaim-sticky-atc__size--static')) {
            sizeButton.querySelector('.klaim-sticky-atc__size-label').textContent = 'ONE SIZE';
            return;
          }
          const selectedVariant = this.stockMap[String(this.getSelectedVariantId())];
          const selectedSize = selectedVariant?.options?.[this.standardSizeIndex] || this.getSelectedOptions()[this.standardSizeIndex];
          const label = sizeButton.querySelector('.klaim-sticky-atc__size-label');
          const nextLabel = selectedSize ? `Size ${selectedSize}` : 'Size';
          if (label && label.textContent.trim() !== nextLabel) label.textContent = nextLabel;
          this.syncStandardVariantRows();
        };

        this.selectStandardVariant = (button) => {
          if (!button) return;
          const variantId = String(button.dataset.klaimStickyVariantId || '');
          if (!variantId) return;

          if (button.dataset.unavailable === 'true') {
            this.closeStandardVariantTray();
            this.dispatchEvent(new CustomEvent('klaim:notify-variant', {
              bubbles: true,
              detail: { variantId, trigger: this.standardSizeToggle }
            }));
            return;
          }

          let options = [];
          try {
            options = JSON.parse(button.dataset.klaimStickyVariantOptions || '[]');
          } catch (error) {}
          const picker = this.productInfo.querySelector('variant-selects');
          const groups = picker
            ? Array.from(picker.children).filter((element) => element.matches('fieldset, .product-form__input--dropdown'))
            : [];
          const group = groups[this.standardSizeIndex];
          const desiredValue = options[this.standardSizeIndex];
          const radio = Array.from(group?.querySelectorAll('input[type="radio"]') || [])
            .find((input) => String(input.value) === String(desiredValue));
          const select = group?.querySelector('select');
          let selectionChanged = false;

          this.closeStandardVariantTray();
          if (radio) {
            if (!radio.checked) {
              selectionChanged = true;
              radio.click();
            }
          } else if (select) {
            if (String(select.value) !== String(desiredValue)) {
              selectionChanged = true;
              select.value = desiredValue;
              select.dispatchEvent(new Event('change', { bubbles: true }));
            }
          } else {
            selectionChanged = true;
            this.productInfo.querySelectorAll('form[action*="/cart/add"] input[name="id"]')
              .forEach((input) => {
                input.value = variantId;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              });
          }

          const label = this.standardSizeToggle?.querySelector('.klaim-sticky-atc__size-label');
          if (label && desiredValue) label.textContent = `Size ${desiredValue}`;
          if (!selectionChanged) this.standardSizeToggle?.focus({ preventScroll: true });
        };

        this.renderBundleSummary = (builder) => {
          const label = this.bundleToggle?.querySelector('.klaim-sticky-atc__size-label');
          if (!label || !builder) return;
          if (builder.classList.contains('klaim-set-builder')) {
            const selections = Array.from(builder.querySelectorAll('[data-set-items] .klaim-set-builder__item')).map((item) => {
              const shirt = item.querySelector('[data-shirt-size]')?.selectedOptions?.[0]?.textContent?.trim();
              const shorts = item.querySelector('[data-short-size]')?.selectedOptions?.[0]?.textContent?.trim();
              return shirt && shorts ? `${shirt}/${shorts}` : shirt || shorts || '';
            }).filter(Boolean);
            label.textContent = selections.length ? `SIZES ${selections.join(' · ')}` : 'SIZES';
            return;
          }
          const selectedSizes = Array.from(builder.querySelectorAll('[data-size-panel]')).map((panel) => {
            return panel.querySelector('.yeezy-slide-builder__size.is-selected')?.dataset.sizeValue || '';
          }).filter(Boolean);
          label.textContent = selectedSizes.length ? `SIZES ${selectedSizes.join(' · ')}` : 'SIZES';
          return;
        };

        this.syncBundleSelect = (select) => {
          const row = select.closest('[data-bundle-size-panel], [data-bundle-set-index]');
          const builder = this.getBundleBuilder();
          if (!row || !builder) return;
          if (builder.classList.contains('yeezy-slide-builder')) {
            const panel = Array.from(builder.querySelectorAll('[data-size-panel]')).find((entry) => entry.dataset.sizePanel === row.dataset.bundleSizePanel);
            const source = Array.from(panel?.querySelectorAll('[data-size-value]') || []).find((entry) => entry.dataset.sizeValue === select.value);
            if (source && !source.disabled) source.click();
          } else {
            const item = Array.from(builder.querySelectorAll('[data-set-items] .klaim-set-builder__item')).find((entry, index) => {
              return entry.dataset.setIndex === row.dataset.bundleSetIndex || String(index) === row.dataset.bundleSetIndex;
            });
            const source = item?.querySelector(`[data-${select.dataset.bundleSetSelect}-size]`);
            if (source) {
              source.value = select.value;
              source.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        };

        this.escapeHtml = (value) => String(value || '')
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#039;');

        this.escapeAttribute = (value) => this.escapeHtml(value).replace(/`/g, '&#096;');

        this.handleSizeRootChange = (event) => {
          if (event.target.matches('[data-bundle-size-select], [data-bundle-set-select]')) {
            this.syncBundleSelect(event.target);
            this.updateStickyState();
          }
        };

        this.handleStickyClick = () => {
          const bundleState = this.getBundleState();
          if (bundleState && !bundleState.available) {
            bundleState.builder.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const selectedTier = bundleState.builder.querySelector('[data-tier].is-selected, [data-pack].is-selected');
            const selectedColor = bundleState.builder.querySelector('[data-product-handle].is-selected');
            const nextChoice = !selectedTier
              ? bundleState.builder.querySelector('[data-tier], [data-pack]')
              : !selectedColor
                ? bundleState.builder.querySelector('[data-product-handle]:not(:disabled)')
                : bundleState.builder.querySelector('.yeezy-slide-builder__size:not(.is-selected):not(:disabled), select:required:invalid, select');
            window.setTimeout(() => nextChoice?.focus(), 350);
            return;
          }
          const button = bundleState?.button || this.getPrimaryButton();
          if (button && !button.disabled) button.click();
        };

        this.handleExpressPayClick = () => {
          const bundleState = this.getBundleState();
          const dynamicButton = this.productInfo.querySelector('.shopify-payment-button__button, .shopify-payment-button__button--unbranded');
          const originalButton = this.getPrimaryButton();
          if (bundleState) {
            if (!bundleState.available) return;
            bundleState.button.click();
            window.setTimeout(() => window.location.assign('/checkout'), 1200);
            return;
          }
          if (!this.isCurrentVariantAvailable(originalButton)) return;
          if (dynamicButton && !dynamicButton.disabled) {
            dynamicButton.click();
            return;
          }
          const form = this.productInfo.querySelector('form[action*="/cart/add"]');
          const variantId = this.getSelectedVariantId();
          if (!form || !variantId) return;
          const body = new FormData(form);
          body.set('id', variantId);
          body.set('quantity', body.get('quantity') || '1');
          fetch(form.action || '/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body
          }).then((response) => {
            if (!response.ok) throw new Error('Express checkout add failed');
            window.location.assign('/checkout');
          }).catch(() => {
            if (originalButton && !originalButton.disabled) originalButton.click();
          });
        };

        this.handleSizeClick = (event) => {
          const bundleBuilder = this.getBundleBuilder();
          if (bundleBuilder) {
            if (event?.target?.closest('[data-klaim-sticky-bundle-toggle]') && this.bundlePickersRoot) {
              this.bundlePickersOpen = !this.bundlePickersOpen;
              this.bundlePickersRoot.hidden = !this.bundlePickersOpen;
              this.bundleToggle?.setAttribute('aria-expanded', this.bundlePickersOpen ? 'true' : 'false');
              this.setAttribute('data-bundle-open', this.bundlePickersOpen ? 'true' : 'false');
              this.syncStickyCoverHeight();
              return;
            }
            bundleBuilder.scrollIntoView({ behavior: 'smooth', block: 'center' });
            window.setTimeout(() => bundleBuilder.querySelector('select, button:not(:disabled)')?.focus(), 350);
            return;
          }
          const sizeButton = this.sizesRoot?.querySelector('.klaim-sticky-atc__size');
          if (sizeButton?.classList.contains('klaim-sticky-atc__size--static')) return;
          if (this.standardVariantTray) {
            event?.preventDefault();
            if (this.standardTrayOpen) this.closeStandardVariantTray(true);
            else this.openStandardVariantTray();
            return;
          }
          const picker = this.productInfo.querySelector('variant-selects, variant-radios, .product-form__input');
          if (!picker) return;
          picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
          window.setTimeout(() => picker.querySelector('label, select, button:not(:disabled)')?.focus(), 350);
        };

        this.syncStickyCoverHeight = () => {
          const height = Math.ceil(this.getBoundingClientRect().height || 0);
          if (height) this.style.setProperty('--klaim-sticky-cover-height', `${height + 12}px`);
        };

        this.updateStickyState = () => {
          if (this.frameRequested) return;
          this.frameRequested = true;
          requestAnimationFrame(() => {
            this.frameRequested = false;
            this.bindBundleBuilder(this.getBundleBuilder());
            const bundleState = this.getBundleState();
            const originalButton = this.getPrimaryButton();
            const componentReady = this.syncComponentOptions();
            const componentBusy = this.componentControls.length && originalButton?.classList.contains('loading');
            const isAvailable = componentReady && !componentBusy && (bundleState ? bundleState.available : this.isCurrentVariantAvailable(originalButton));
            const isMobile = window.matchMedia('(max-width: 749px)').matches;
            const isQuickAdd = Boolean(this.closest('quick-add-modal'));
            const shouldShow = isMobile && !isQuickAdd;

            this.dataset.visible = shouldShow ? 'true' : 'false';
            this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
            if (!shouldShow && this.standardTrayOpen) this.closeStandardVariantTray();
            this.stickyButton.disabled = bundleState ? false : !isAvailable;
            this.stickyButton.tabIndex = shouldShow ? 0 : -1;
            if (this.expressPayButton) {
              this.expressPayButton.disabled = !isAvailable;
              this.expressPayButton.tabIndex = shouldShow ? 0 : -1;
            }
            if (this.desktopButton && !bundleState) this.desktopButton.disabled = !isAvailable;

            if (bundleState) {
              this.renderBundleSizes(bundleState.builder);
              this.renderBundleSummary(bundleState.builder);
              this.setStickyCopy(bundleState.label, bundleState.price);
            } else {
              this.renderStandardSize();
              this.setStickyCopy(componentBusy ? 'ADDING' : !componentReady ? 'UPDATING' : isAvailable ? 'ADD TO CART' : 'SOLD OUT', isAvailable ? this.getCurrentPrice() : '');
              const sizeButton = this.sizesRoot?.querySelector('.klaim-sticky-atc__size');
              if (sizeButton) sizeButton.tabIndex = shouldShow && !sizeButton.classList.contains('klaim-sticky-atc__size--static') ? 0 : -1;
            }
            this.syncStickyCoverHeight();
          });
        };

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, event => {
            const variant = event?.data?.variant;
            if (this.componentControls.length && variant && this.stockMap[String(variant.id)]) this.stockMap[String(variant.id)].online = variant.available;
            this.updateStickyState();
          });
        }

        this.sizesRoot?.addEventListener('change', this.handleSizeRootChange);
        this.sizesRoot?.addEventListener('click', this.handleComponentClick);
        this.productInfo.addEventListener('change', this.updateStickyState);
        this.stickyButton.addEventListener('click', this.handleStickyClick);
        this.desktopButton?.addEventListener('click', this.handleStickyClick);
        this.expressPayButton?.addEventListener('click', this.handleExpressPayClick);
        this.handleSizesClick = (event) => {
          const variant = event.target.closest('[data-klaim-sticky-variant-id]');
          if (variant) {
            event.preventDefault();
            event.stopPropagation();
            this.selectStandardVariant(variant);
            return;
          }
          if (event.target.closest('.klaim-sticky-atc__size')) this.handleSizeClick(event);
        };
        this.handleDocumentPointerDown = (event) => {
          if (!event.target.closest('[data-klaim-component-control]')) this.closeComponentMenus();
          if (!this.standardTrayOpen || this.standardSelector?.contains(event.target)) return;
          this.closeStandardVariantTray();
        };
        this.handleDocumentKeydown = (event) => {
          if (event.key === 'Escape') this.closeComponentMenus(true);
          if (event.key !== 'Escape' || !this.standardTrayOpen) return;
          event.preventDefault();
          this.closeStandardVariantTray(true);
        };
        this.sizesRoot?.addEventListener('click', this.handleSizesClick);
        document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
        document.addEventListener('keydown', this.handleDocumentKeydown);
        this.stateObserver = new MutationObserver(this.updateStickyState);
        this.stateObserver.observe(this.productInfo, {
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-pressed', 'data-size-value'],
          childList: true,
          characterData: true,
          subtree: true
        });
        this.handleResize = () => {
          if (!window.matchMedia('(max-width: 749px)').matches && this.standardTrayOpen) {
            this.closeStandardVariantTray();
          }
          this.updateStickyState();
        };
        window.addEventListener('resize', this.handleResize);
        this.updateStickyState();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.stateObserver?.disconnect();
        this.sizesRoot?.removeEventListener('change', this.handleSizeRootChange);
        this.sizesRoot?.removeEventListener('click', this.handleComponentClick);
        this.productInfo?.removeEventListener('change', this.updateStickyState);
        this.stickyButton?.removeEventListener('click', this.handleStickyClick);
        this.desktopButton?.removeEventListener('click', this.handleStickyClick);
        this.expressPayButton?.removeEventListener('click', this.handleExpressPayClick);
        this.sizesRoot?.removeEventListener('click', this.handleSizesClick);
        document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        this.unbindBundleBuilder?.();
        window.removeEventListener('resize', this.handleResize);
      }
    }
  );
}
